/** Same-tab delivery work belongs to a principal; permission belongs to an opening. */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { confirmOrderDelivery, updateOrderDelivery, uploadOrderDeliveryMedia, failOrderDelivery, getOrderDeliveryConfirmation, getOrderDeliveryAttempt, getOrderDeliveryAmendment, getOrderDeliveryFailure } from '@/api/deliveries';
import {
  deliveryPrincipalKey, getDeliveryWorkspace, createDeliveryWorkspace, updateDeliveryWorkspace,
  clearDeliveryWorkspace, acquireDeliveryAction, releaseDeliveryAction, getDeliveryAction,
  subscribeDeliveryWorkspaces, getDeliveryWorkspaceRevision,
  listDeliveryWorkspaces, hasDeliveryWorkspaceWork,
} from '@/utils/deliveryDraftStore';
import { saveDelivery } from '@/utils/deliveryOutbox';
import { withDeliveryCopyOperation } from '@/utils/deliveryCopyOperation';
import { withPreparedDeliveryOperation, withLegacyDeliveryOperation } from '@/utils/deliveryCopyAdmission';
import { assertPhotoDispatch } from '@/utils/deliveryPhotoDraftPersistence';
import {
  buildDeliveryConfirmationForm, buildDeliveryEditPayload, buildDeliveryMediaForm,
  buildOfflineDeliveryPayload, freezeDeliveryFields, deliveryTimeToIso,
} from '@/utils/deliveryFormPayload';
import { requestDeliveryLocation } from '@/utils/deliveryLocation';
import { useSavedDeliveryMedia } from '@/hooks/useSavedDeliveryMedia';
import { useSavedPhotoRecovery } from '@/hooks/useSavedPhotoRecovery';
import { useDeliveryPhotoPreparation, DELIVERY_PHOTO_PREPARATION_ENABLED } from '@/hooks/useDeliveryPhotoPreparation';
import { validatePreparationBatch } from '@/utils/prepareDeliveryPhoto';
import { createDeliveryPhotoAttempt, copyDeliveryPhotoAttempt, buildIdentifiedDeliveryForm, readDeliveryPhotoReceipt,
  createDeliveryPhotoAmendment, buildIdentifiedAmendment, readDeliveryPhotoAmendment } from '@/utils/deliveryPhotoSubmission';
import { createDeliveryFailureAttempt, copyDeliveryFailureAttempt, buildIdentifiedFailure, readDeliveryFailureReceipt } from '@/utils/deliveryFailureSubmission';

const initialFields = (order, editing) => {
  const dc = editing ? order.delivery_confirmation || {} : {};
  const date = dc.time_delivered ? new Date(dc.time_delivered) : new Date();
  const time = Number.isNaN(date.getTime()) ? '' : `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  return {
    receivedBy: dc.received_by || '', receivedByRelationship: dc.received_by_relationship || '',
    notes: dc.notes || '', signatureData: null, photos: [], video: null,
    deliveryTime: time, deliveryTimeIso: deliveryTimeToIso(time, date),
    gpsLocation: dc.gps_location || null, gpsStatus: editing ? 'success' : 'pending', gpsError: null,
  };
};
const textEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export const useDeliveryForm = ({ isOpen, order, openingId, sessionBinding: admittedBinding, onDeliveryConfirmed, onClose }) => {
  const { getToken, sessionBinding } = useAuth();
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  useSyncExternalStore(subscribeDeliveryWorkspaces, getDeliveryWorkspaceRevision);
  // A new authentication revision never rebrands a form that was already open.
  const baseOpening = useMemo(() => ({
    binding: admittedBinding || sessionBinding,
    principal: deliveryPrincipalKey(admittedBinding || sessionBinding),
    orderId: order?.id, kind: order?.status === 'delivered' ? 'amendment' : 'confirmation',
  // Only a parent-qualified opening may capture new authority.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isOpen, openingId, order?.id]);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const explicitlySelected = selectedOperation?.base === baseOpening;
  const opening = useMemo(() => explicitlySelected ? { ...baseOpening, kind: selectedOperation.kind } : baseOpening,
    [baseOpening, explicitlySelected, selectedOperation]);
  const latest = useRef(null);
  latest.current = { opening, isOpen, sessionBinding };
  const mounted = useRef(false);
  const initializedOpening = useRef(null);
  const reviewedUnknown = useRef(null);
  const baselineOpening = useRef(null);
  const autoReceiptCheck = useRef(null);
  const restoredPhotoWork = useRef(null);
  const [readyOpening, setReadyOpening] = useState(null);
  const [reviewedOpening, setReviewedOpening] = useState(null);
  const [recoveryOpening, setRecoveryOpening] = useState(null);
  const [uncertaintyReviewed, setUncertaintyReviewed] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [photoOutcome, setPhotoOutcome] = useState('');
  const [offlinePromptOpen, setOfflinePromptOpen] = useState(false);
  const [discardSelection, setDiscardSelection] = useState(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const getWork = (kind = opening.kind) => getDeliveryWorkspace(opening.principal, opening.orderId, kind);
  const owns = () => mounted.current && latest.current.isOpen && latest.current.opening === opening
    && !!opening.principal && opening.binding?.revision === getActiveSessionRevision()
    && latest.current.sessionBinding?.revision === opening.binding.revision
    && deliveryPrincipalKey(latest.current.sessionBinding) === opening.principal;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!isOpen || !opening.principal || !opening.orderId || !owns() || initializedOpening.current === opening) return;
    initializedOpening.current = opening;
    // Retire untouched shells from earlier openings, including route unmounts.
    // Reinitialize them from this opening's current order, not an old snapshot.
    listDeliveryWorkspaces(opening.principal, opening.orderId).forEach((candidate) => {
      if (!hasDeliveryWorkspaceWork(candidate)) clearDeliveryWorkspace(candidate);
    });
    const existing = getWork();
    const failure = getWork('failure');
    let work = createDeliveryWorkspace(opening.principal, opening.orderId, opening.kind, initialFields(order, opening.kind === 'amendment'));
    if (!existing && opening.kind === 'amendment') {
      work = updateDeliveryWorkspace(work, { checkpoint: { baselineText: buildDeliveryEditPayload(work.fields) } }) || work;
    }
    if (!DELIVERY_PHOTO_PREPARATION_ENABLED && opening.kind === 'confirmation') createDeliveryWorkspace(opening.principal, opening.orderId, 'failure', { reason: '', details: '' });
    setReadyOpening(opening);
    setRecoveryOpening(existing || failure?.fields.reason || failure?.fields.details || failure?.attempt ? opening : null);
    setReviewedOpening(null);
    setUncertaintyReviewed(null);
    reviewedUnknown.current = null;
    setOfflinePromptOpen(false);
    setDiscardSelection(null);
    setUploadProgress(0);
    setPhotoOutcome('');
    if (!existing && opening.kind === 'confirmation') {
      requestDeliveryLocation(navigator.geolocation).then((location) => {
        if (!owns()) return;
        const current = getWork();
        if (current?.id !== work.id || getDeliveryAction(current) || restoredPhotoWork.current === current.id || current.fields.locationCleared) return;
        updateDeliveryWorkspace(current, { fields: { ...current.fields, gpsLocation: location, gpsStatus: 'success', gpsError: null } });
      }).catch((error) => {
        if (!owns()) return;
        const current = getWork();
        if (current?.id !== work.id || getDeliveryAction(current) || restoredPhotoWork.current === current.id || current.fields.locationCleared) return;
        updateDeliveryWorkspace(current, { fields: { ...current.fields, gpsStatus: 'error', gpsError: error.message } });
      });
    }
  // Opening owns the complete asynchronous lifetime, not changing order-list objects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening, isOpen]);

  const work = getWork();
  const failureWork = getWork('failure');
  const opposite = getWork(opening.kind === 'amendment' ? 'confirmation' : 'amendment');
  const kindConflict = (!!opposite && !(DELIVERY_PHOTO_PREPARATION_ENABLED && explicitlySelected
    && (!hasDeliveryWorkspaceWork(opposite) || opposite.attempt?.kind === opposite.kind || !!opposite.photoDraftRevision)))
    || (opening.kind === 'amendment' && !!failureWork?.attempt)
    || (DELIVERY_PHOTO_PREPARATION_ENABLED && hasDeliveryWorkspaceWork(failureWork));
  const isVisible = !!isOpen && !!opening.principal && opening.binding?.revision === revision
    && sessionBinding?.revision === opening.binding.revision && deliveryPrincipalKey(sessionBinding) === opening.principal;
  const isSubmitting = !!work && !!getDeliveryAction(work);
  // Ordinary edit drafts can be saved directly. A previous operation with an
  // unresolved outcome still requires its separate review flow.
  const isPlainEditDraft = opening.kind === 'amendment' && !!work && !kindConflict
    && !work.attempt && !work.savedMediaRemoval;
  // This reminder belongs to a returning draft, not edits begun in this opening.
  const hasPendingEdits = recoveryOpening === opening && isPlainEditDraft && hasDeliveryWorkspaceWork(work);
  const needsRecovery = !DELIVERY_PHOTO_PREPARATION_ENABLED && recoveryOpening === opening && reviewedOpening !== opening && !isPlainEditDraft;
  const uncertainAttempt = [work?.attempt, failureWork?.attempt].find((attempt) => attempt?.status === 'unknown');
  const needsUncertaintyReview = !!uncertainAttempt && uncertaintyReviewed !== uncertainAttempt;
  const canAct = (allowSavedMediaRecovery = false) => {
    const unknown = [getWork()?.attempt, getWork('failure')?.attempt].find((attempt) => attempt?.status === 'unknown');
    return owns() && !photoRecovery.isOpen && !discardSelection && readyOpening === opening && (!DELIVERY_PHOTO_PREPARATION_ENABLED || (photoPreparation.ready
      && (!photoPreparation.otherDraft || explicitlySelected)
      && (opening.kind !== 'amendment' || !!getWork()?.checkpoint?.amendmentRevision || !!getWork()?.attempt))) && !needsRecovery && (!unknown || reviewedUnknown.current === unknown) && !kindConflict && !!getWork()
      && (allowSavedMediaRecovery || !getWork()?.savedMediaRemoval);
  };
  const fields = work?.fields || initialFields(order || {}, opening.kind === 'amendment');
  const isEditing = opening.kind === 'amendment';
  const isFailureAttempt = DELIVERY_PHOTO_PREPARATION_ENABLED && work?.attempt?.kind === 'failure';
  const acceptedFailure = isFailureAttempt && work.attempt.status === 'accepted';
  const photoRecovery = useSavedPhotoRecovery({ enabled: DELIVERY_PHOTO_PREPARATION_ENABLED && isVisible,
    opening, work, owns, getToken, isTabSaved: () => photoPreparation.saved,
    onSelected: () => {
      setRecoveryOpening(null); setReviewedOpening(null); setUncertaintyReviewed(null);
      reviewedUnknown.current = null; baselineOpening.current = null; setPhotoOutcome('');
    } });
  const photoPreparation = useDeliveryPhotoPreparation({ enabled: DELIVERY_PHOTO_PREPARATION_ENABLED && isVisible,
    opening, work, owns, canAct, onRestored: (id) => { restoredPhotoWork.current = id; },
    discoverOther: true, paused: photoRecovery.isOpen, safelyRetained: photoRecovery.savedCurrent });

  useEffect(() => {
    if (photoRecovery.isOpen || !DELIVERY_PHOTO_PREPARATION_ENABLED || !isEditing || !owns() || !photoPreparation.ready || !work
        || (photoPreparation.otherDraft && !explicitlySelected) || work.attempt || work.checkpoint?.amendmentRevision || baselineOpening.current === opening || getDeliveryAction(work)) return;
    baselineOpening.current = opening;
    const token = getToken();
    if (!token) return;
    const lease = acquireDeliveryAction(work, 'read-amendment-baseline');
    if (!lease) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    (async () => {
      try {
        const result = await getOrderDeliveryConfirmation(opening.orderId, { token, signal: controller.signal });
        if (controller.signal.aborted || !owns() || getWork()?.id !== work.id || getDeliveryAction(getWork()) !== lease) return;
        const dc = result?.confirmation;
        const baseline = work.checkpoint?.baselineText;
        if (result?.order_id !== opening.orderId || result.status !== 'delivered' || !/^[a-f0-9]{64}$/.test(result.amendment_revision || '')
            || !baseline || Object.keys(baseline).some((key) => (dc?.[key] ?? '') !== baseline[key])) {
          throw new Error('The saved delivery changed. Your edits are retained; reopen from the current order for review.');
        }
        const updated = updateDeliveryWorkspace(getWork(), { checkpoint: { ...work.checkpoint, amendmentRevision: result.amendment_revision } }, lease);
        await photoPreparation.commitRecovery(updated, lease);
      } catch (error) { if (owns()) setPhotoOutcome(error.message || 'Could not read the saved delivery. Close and reopen to try again.'); }
      finally { clearTimeout(timeout); releaseDeliveryAction(lease); }
    })();
    // The saved revision belongs to this opening, not changing order-list props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening, photoPreparation.ready, work?.id, photoRecovery.isOpen]);

  const change = (name, value, kind = opening.kind) => {
    if (!canAct()) return false;
    const current = getWork(kind);
    if (!current || getDeliveryAction(current) || (DELIVERY_PHOTO_PREPARATION_ENABLED && getWork()?.attempt)) return false;
    let deliveryTimeIso;
    if (name === 'deliveryTime') {
      try { deliveryTimeIso = deliveryTimeToIso(value); } catch { /* Validate incomplete input at submission, not while typing. */ }
    }
    return !!updateDeliveryWorkspace(current, { fields: { ...current.fields, [name]: value,
      ...(name === 'deliveryTime' ? { deliveryTimeIso } : {}) }, attempt: null });
  };
  const clearLocation = () => {
    if (!canAct() || isEditing) return;
    const current = getWork();
    if (!current || current.attempt || getDeliveryAction(current)) return;
    updateDeliveryWorkspace(current, { fields: { ...current.fields, gpsLocation: null, gpsStatus: 'cleared', gpsError: null, locationCleared: true } });
  };
  const admit = (kind, action, allowSavedMediaRecovery = false) => {
    if (!canAct(allowSavedMediaRecovery)) return null;
    const current = getWork(kind);
    if (!current) return null;
    const token = getToken();
    if (!token || !owns()) return null;
    const lease = acquireDeliveryAction(current, action);
    return lease ? { current, token, lease } : null;
  };
  const savedMedia = useSavedDeliveryMedia({
    enabled: isVisible && isEditing,
    opening, owns, getWork, getToken,
    canAct: () => canAct(true) && (!DELIVERY_PHOTO_PREPARATION_ENABLED || !getWork()?.attempt),
    admit: (action) => admit('amendment', action, true),
    onChanged: onDeliveryConfirmed,
  });
  const finishUi = (response, message) => {
    if (!owns()) return;
    toast.success(message);
    onDeliveryConfirmed?.(opening.orderId, response);
    if (owns()) onClose?.();
  };

  // Same form owner and action lease as the legacy workflow. Persist the
  // immutable attempt before transport, and observe receipts before any retry.
  const submitPreparedDelivery = async (reviewOnly = false, copyContext) => {
    if (!copyContext) {
      try {
        const operation = reviewOnly ? withDeliveryCopyOperation : withPreparedDeliveryOperation;
        await operation(opening.orderId, (context) => submitPreparedDelivery(reviewOnly, context), { requireCrossContext: true, isCurrent: owns });
      } catch (error) { if (owns()) setPhotoOutcome(error.message); }
      return;
    }
    if (!owns() || photoRecovery.isOpen || discardSelection || needsRecovery || kindConflict || !photoPreparation.ready
        || photoPreparation.busy || !photoPreparation.saved) return;
    const value = getWork();
    if (value?.attempt?.kind === 'failure') return; // Never convert a report into confirmation.
    if (!value || (!value.attempt && (reviewOnly || !value.fields.receivedBy.trim()))) return;
    if (!value.attempt && !isEditing && !value.fields.deliveryTimeIso) {
      setPhotoOutcome('Choose the delivery time before confirming.'); return;
    }
    if (!value.attempt && ((isEditing && order?.status !== 'delivered') || (!isEditing && order?.status === 'delivered'))) {
      setPhotoOutcome('The order status changed. This unsent draft was retained, not converted or submitted.'); return;
    }
    if (!reviewOnly && !canAct()) return;
    const token = getToken();
    if (!token || !owns()) return;
    const lease = acquireDeliveryAction(value, reviewOnly ? 'check-photo-receipt' : 'confirm-prepared-photos');
    if (!lease) return;
    let attempt;
    let acceptedObserved = false;
    const stillOwns = () => owns() && getWork()?.id === value.id && getDeliveryAction(getWork()) === lease;
    const commit = async (next) => {
      if (!stillOwns()) throw new DOMException('Opening retired', 'AbortError');
      const updated = updateDeliveryWorkspace(getWork(), { attempt: next }, lease);
      if (!updated) throw new Error('Draft changed; retain it for review.');
      attempt = updated.attempt;
      await photoPreparation.commitRecovery(updated, lease, copyContext);
      if (!stillOwns()) throw new DOMException('Opening retired', 'AbortError');
      attempt = getWork().attempt;
    };
    const observe = async () => {
      const result = await (isEditing ? getOrderDeliveryAmendment : getOrderDeliveryAttempt)(opening.orderId, attempt.captureId, { token });
      if (!stillOwns()) throw new DOMException('Opening retired', 'AbortError');
      if (isEditing) {
        const observed = readDeliveryPhotoAmendment(attempt, opening.orderId, result);
        attempt = observed.attempt;
        return observed.state;
      }
      const observed = readDeliveryPhotoReceipt(attempt, opening.orderId, result);
      attempt = observed.attempt;
      return observed.state;
    };
    const complete = async () => {
      acceptedObserved = true;
      await commit({ ...attempt, status: 'accepted' });
      if (reviewOnly && isEditing) { setPhotoOutcome('Delivery confirmed. Your saved copy is still on this device.'); return; }
      await photoPreparation.complete(getWork(), lease, copyContext);
      if (!stillOwns()) return;
      clearDeliveryWorkspace(getWork(), lease);
      finishUi({ order_id: opening.orderId, status: 'delivered' }, isEditing ? 'Delivery updated' : 'Delivery confirmed!');
    };
    try {
      setPhotoOutcome(reviewOnly ? 'Checking delivery status…' : '');
      let state = 'not-found';
      if (value.attempt) {
        attempt = copyDeliveryPhotoAttempt(value.attempt);
        if (attempt.kind !== opening.kind) throw new Error('Saved work belongs to another operation; nothing was sent.');
        state = await observe();
        if (state === 'accepted') { await complete(); return; }
        if ((isEditing && order?.status !== 'delivered') || (!isEditing && order?.status === 'delivered' && state !== 'video-outstanding')) {
          throw new Error('The order status changed and this attempt is not fully acknowledged. Work is retained; nothing was resent.');
        }
        // A previously observed acceptance must never regress into resend.
        if (attempt.status === 'accepted') throw new Error('Accepted receipt is unavailable; keep the draft and check again.');
        if (reviewOnly) {
          await commit({ ...attempt, status: 'unknown' });
          reviewedUnknown.current = getWork().attempt;
          setUncertaintyReviewed(getWork().attempt);
          setPhotoOutcome(isEditing ? (state === 'media-outstanding'
            ? 'Text is unchanged or already accepted. Only the saved media remain; use Update to retry them.'
            : 'No text receipt found yet. Nothing was resent; use Update to retry the exact amendment.') : state === 'video-outstanding'
            ? 'Photos and delivery were accepted. Only the saved video remains. Use Confirm to retry that video.'
            : 'Delivery is not confirmed yet. Your work is saved. Tap Confirm Delivery to try again safely.');
          return;
        }
      } else {
        attempt = isEditing ? await createDeliveryPhotoAmendment(value.fields, value.checkpoint) : await createDeliveryPhotoAttempt(value.fields);
        if (!stillOwns()) return;
        if (isEditing) state = attempt.amendment.textRequired ? 'text-outstanding' : 'media-outstanding';
      }
      const phase = isEditing ? (state === 'text-outstanding' ? 'text' : 'media') : state === 'video-outstanding' ? 'video' : 'core';
      const payload = isEditing ? await buildIdentifiedAmendment(attempt, phase) : await buildIdentifiedDeliveryForm(attempt, { phase });
      if (!stillOwns()) return;
      await commit({ ...attempt, status: 'pending' });
      await assertPhotoDispatch(opening.principal, opening.orderId, attempt.kind, attempt.captureId);
      // No asynchronous gap between the final authority check and dispatch.
      if (!stillOwns()) return;
      setUploadProgress(0);
      const options = { token, onUploadProgress: (event) => {
        if (stillOwns() && event.total > 0) setUploadProgress(Math.round(event.loaded * 100 / event.total));
      } };
      if (phase === 'text') await updateOrderDelivery(opening.orderId, payload, options);
      else if (phase === 'core') await confirmOrderDelivery(opening.orderId, payload, options);
      else await uploadOrderDeliveryMedia(opening.orderId, payload, options);
      if (!stillOwns()) return;
      state = await observe();
      if (phase === 'text' && state === 'media-outstanding') {
        // Continue the user's original two-step action, never replay text.
        // Freeze and commit the text receipt's revision before sending media.
        const media = await buildIdentifiedAmendment(attempt, 'media');
        if (!stillOwns()) return;
        await commit({ ...attempt, status: 'pending' });
        await assertPhotoDispatch(opening.principal, opening.orderId, attempt.kind, attempt.captureId);
        if (!stillOwns()) return;
        await uploadOrderDeliveryMedia(opening.orderId, media, options);
        if (!stillOwns()) return;
        state = await observe();
      }
      if (state === 'accepted') await complete();
      else throw new Error('Acceptance is incomplete. Check the receipt before a manual retry.');
    } catch (error) {
      if (!stillOwns()) return; // durable pending remains recoverable as unknown
      if (attempt) {
        const retained = { ...attempt, status: acceptedObserved || attempt.status === 'accepted' ? 'accepted' : 'unknown' };
        try { await commit(retained); } catch { /* Storage owner reports unsaved; never send after it fails. */ }
      }
      if (stillOwns()) {
        reviewedUnknown.current = null;
        setUncertaintyReviewed(null);
        setPhotoOutcome(error.response?.status === 422
          ? 'The server could not process this request. Your work is kept. Check delivery status before trying again.'
          : error.response || reviewOnly ? 'We couldn’t confirm this delivery. Your work is saved on this device.'
            : error.message || 'Could not confirm delivery. Your work is kept. Check delivery status when connected.');
        if (!reviewOnly) toast.error('Delivery not verified. Your work is kept.');
      }
    } finally { releaseDeliveryAction(lease); }
  };

  const submitPreparedFailure = async (reviewOnly = false, copyContext) => {
    if (!copyContext) {
      try {
        const operation = reviewOnly ? withDeliveryCopyOperation : withPreparedDeliveryOperation;
        await operation(opening.orderId, (context) => submitPreparedFailure(reviewOnly, context), { requireCrossContext: true, isCurrent: owns });
      } catch (error) { if (owns()) setPhotoOutcome(error.message); }
      return;
    }
    if (!owns() || photoRecovery.isOpen || isEditing || discardSelection || needsRecovery || kindConflict || !photoPreparation.ready
        || photoPreparation.busy || !photoPreparation.saved || (!reviewOnly && !canAct())) return;
    const value = getWork();
    if (!value || (value.attempt && value.attempt.kind !== 'failure')
        || (!value.attempt && (reviewOnly || !value.fields.failureReason))) return;
    if (!value.attempt && order?.status !== 'on_route') {
      setPhotoOutcome('This order is no longer on route. The unsent draft is retained; nothing was sent.'); return;
    }
    const token = getToken();
    if (!token || !owns()) return;
    const lease = acquireDeliveryAction(value, reviewOnly ? 'check-failure-receipt' : 'report-failure');
    if (!lease) return;
    let attempt; let acceptedObserved = false; let dispatched = false;
    const stillOwns = () => owns() && getWork()?.id === value.id && getDeliveryAction(getWork()) === lease;
    const commit = async (next) => {
      if (!stillOwns()) throw new DOMException('Opening retired', 'AbortError');
      const updated = updateDeliveryWorkspace(getWork(), { attempt: next }, lease);
      if (!updated) throw new Error('Draft changed; work retained.');
      attempt = updated.attempt;
      await photoPreparation.commitRecovery(updated, lease, copyContext);
      if (!stillOwns()) throw new DOMException('Opening retired', 'AbortError');
      attempt = getWork().attempt;
    };
    const observe = async () => {
      const result = await getOrderDeliveryFailure(opening.orderId, attempt.captureId, { token });
      if (!stillOwns()) throw new DOMException('Opening retired', 'AbortError');
      return readDeliveryFailureReceipt(attempt, opening.orderId, result);
    };
    const accept = async () => {
      acceptedObserved = true;
      await commit({ ...attempt, status: 'accepted' });
      // Do not close, delete photos or create a legacy-outbox copy. Disposal
      // is a separate, explicit local action after durable acknowledgement.
      setPhotoOutcome('Failure report accepted. Photos were not uploaded and remain on this device until you discard this local copy.');
      if (!reviewOnly) onDeliveryConfirmed?.(opening.orderId, { order_id: opening.orderId, status: 'failed' });
    };
    try {
      setPhotoOutcome('');
      if (value.attempt) {
        attempt = copyDeliveryFailureAttempt(value.attempt);
        if (await observe() === 'accepted') { await accept(); return; }
        if (attempt.status === 'accepted') throw new Error('Previously accepted receipt is unavailable. Keep the local copy and check again; nothing was resent.');
        if (reviewOnly) {
          await commit({ ...attempt, status: 'unknown' });
          reviewedUnknown.current = getWork().attempt;
          setUncertaintyReviewed(getWork().attempt);
          setPhotoOutcome('No receipt found yet. Nothing was resent. You may retry the exact saved failure report manually.');
          return;
        }
      } else {
        attempt = createDeliveryFailureAttempt(value.fields);
      }
      if (order?.status !== 'on_route') throw new Error('This order is no longer on route. Work is retained; no failure report was sent.');
      const payload = buildIdentifiedFailure(attempt);
      await commit({ ...attempt, status: 'pending' });
      await assertPhotoDispatch(opening.principal, opening.orderId, attempt.kind, attempt.captureId);
      if (!stillOwns()) return;
      dispatched = true;
      await failOrderDelivery(opening.orderId, payload, { token });
      dispatched = false;
      if (!stillOwns()) return;
      if (await observe() !== 'accepted') throw new Error('Acceptance is not verified. Check the receipt before retrying.');
      await accept();
    } catch (error) {
      if (!stillOwns()) return;
      if (attempt) {
        // Only explicit request-validation responses prove this dispatch was
        // rejected. Missing, conflicting or failed observations stay unknown.
        const status = acceptedObserved || attempt.status === 'accepted' ? 'accepted'
          : dispatched && [400, 422].includes(error.response?.status) ? 'rejected' : 'unknown';
        try { await commit({ ...attempt, status }); } catch { /* Storage owner blocks further sends/disposal. */ }
      }
      if (stillOwns()) {
        reviewedUnknown.current = null; setUncertaintyReviewed(null);
        setPhotoOutcome(error.message || 'Failure report outcome is uncertain. Work retained; check its receipt.');
      }
    } finally { releaseDeliveryAction(lease); }
  };

  useEffect(() => {
    const captureId = photoPreparation.returningAttemptId;
    if (!DELIVERY_PHOTO_PREPARATION_ENABLED || !captureId || !owns() || !photoPreparation.ready || !photoPreparation.saved
        || photoPreparation.busy || photoRecovery.isOpen || discardSelection || kindConflict || getDeliveryAction(work)
        || work?.attempt?.captureId !== captureId
        || (autoReceiptCheck.current?.opening === opening && autoReceiptCheck.current.captureId === captureId)) return;
    // Once per returning opening, not a polling/retry loop. Only the existing
    // read-only receipt path runs; failed reads never authorize a resend.
    autoReceiptCheck.current = { opening, captureId };
    void (work.attempt.kind === 'failure' ? submitPreparedFailure(true) : submitPreparedDelivery(true));
    // Operations capture this opening and recheck its lease/session after I/O.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening, work, photoPreparation.returningAttemptId, photoPreparation.ready, photoPreparation.saved,
    photoPreparation.busy, photoRecovery.isOpen, discardSelection, kindConflict]);

  const submit = async (kind, copyContext) => {
    if (DELIVERY_PHOTO_PREPARATION_ENABLED) {
      if (kind !== 'failure') await submitPreparedDelivery();
      else await submitPreparedFailure();
      return;
    }
    if (!copyContext) {
      try { await withLegacyDeliveryOperation(opening.orderId, (context) => submit(kind, context), { isCurrent: owns }); }
      catch (error) { if (owns()) toast.error(error.message); }
      return;
    }
    const current = getWork(kind);
    if (!current || (kind === 'failure' ? !current.fields.reason : !current.fields.receivedBy.trim())) return;
    if (kind === 'amendment' && textEqual(current.checkpoint?.acknowledgedText || current.checkpoint?.baselineText, buildDeliveryEditPayload(current.fields))
      && !current.fields.photos.length && !current.fields.video) {
      if (canAct()) toast.error('No changes to submit. Delivery time is not changed by an amendment.');
      return;
    }
    const admission = admit(kind, kind === 'failure' ? 'fail' : isEditing ? 'update' : 'confirm');
    if (!admission) return;
    const { token, lease } = admission;
    let active = admission.current;
    const progress = (event) => {
      if (owns() && getDeliveryAction(active) === lease && event.total > 0) setUploadProgress(Math.round(event.loaded * 100 / event.total));
    };
    let snapshot = active.attempt?.fields;
    let dispatched = false;
    let stage = kind === 'failure' ? 'failure' : isEditing ? 'text' : 'confirmation';
    const patch = (value) => { active = updateDeliveryWorkspace(active, value, lease) || active; };
    try {
      snapshot = snapshot || freezeDeliveryFields(active.fields);
      patch({ attempt: { fields: snapshot, status: 'pending', stage } });
      setUploadProgress(0);
      let response;
      if (kind === 'failure') {
        dispatched = true;
        response = await failOrderDelivery(opening.orderId, { reason: snapshot.reason, details: snapshot.details }, { token });
      } else if (isEditing) {
        const text = buildDeliveryEditPayload(snapshot);
        if (!textEqual(active.checkpoint?.acknowledgedText || active.checkpoint?.baselineText, text)) {
          dispatched = true;
          await updateOrderDelivery(opening.orderId, text, { token });
          patch({ checkpoint: { acknowledgedText: text }, attempt: { fields: snapshot, status: 'media-pending', stage: 'media' } });
        }
        dispatched = false;
        stage = 'media';
        if (snapshot.photos.length || snapshot.video) {
          if (!owns()) {
            patch({ attempt: { fields: snapshot, status: 'media-pending', stage } });
            return;
          }
          patch({ attempt: { fields: snapshot, status: 'pending', stage } });
          const media = buildDeliveryMediaForm(snapshot);
          dispatched = true;
          await uploadOrderDeliveryMedia(opening.orderId, media, { token, onUploadProgress: progress });
        }
        response = { status: 'delivered' };
      } else {
        const payload = buildDeliveryConfirmationForm(snapshot);
        dispatched = true;
        response = await confirmOrderDelivery(opening.orderId, payload, { token, onUploadProgress: progress });
      }
      clearDeliveryWorkspace(active, lease);
      finishUi(response || { status: kind === 'failure' ? 'failed' : 'delivered' }, kind === 'failure' ? 'Delivery marked as failed' : isEditing ? 'Delivery updated' : 'Delivery confirmed!');
    } catch (error) {
      const unknown = dispatched && !error.response;
      patch({ attempt: { fields: snapshot || active.fields, status: unknown ? 'unknown' : 'rejected', stage,
        message: unknown ? 'The server outcome is unknown. Check the current delivery before manually retrying; a retry may duplicate already accepted work.' : error.response?.data?.detail || (dispatched ? 'The server returned an error. Your work is retained; review the delivery before retrying.' : 'This request could not be prepared. Your work is retained; review the fields before retrying.') } });
      if (owns()) {
        setUncertaintyReviewed(null);
        if (unknown && kind === 'confirmation') setOfflinePromptOpen(true);
        else toast.error(unknown ? 'Server outcome unknown. Review before retrying.' : error.response?.data?.detail || 'Unable to complete this action. Your work is retained.');
      }
    } finally { releaseDeliveryAction(lease); }
  };

  const handleSaveOffline = async () => {
    if (DELIVERY_PHOTO_PREPARATION_ENABLED) {
      // Keep one durable owner. Never copy identified work into the legacy
      // outbox, whose resend path does not understand its receipt contract.
      const current = getWork();
      if (!owns() || discardSelection || needsRecovery || kindConflict || !photoPreparation.ready || !photoPreparation.saved
          || current?.attempt?.status !== 'unknown') return;
      const lease = acquireDeliveryAction(current, 'keep-prepared-attempt');
      if (!lease) return;
      try {
        await photoPreparation.commitRecovery(current, lease);
        if (!owns()) return;
        setOfflinePromptOpen(false);
        toast.success('Saved on this device, not confirmed by the server. Resume from saved work when connected.');
        onClose?.();
      } catch { if (owns()) toast.error('Could not verify the save. Keep this form open; your work is retained.'); }
      finally { releaseDeliveryAction(lease); }
      return;
    }
    // Save preserves the exact failed confirmation, never an amendment/failure.
    if (isEditing || !owns() || needsRecovery || kindConflict) return;
    const current = getWork();
    if (current?.attempt?.status !== 'unknown') return;
    const lease = acquireDeliveryAction(current, 'save');
    if (!lease) return;
    try {
      await saveDelivery(buildOfflineDeliveryPayload({ ...current.attempt.fields, orderId: opening.orderId }), {
        isCurrent: () => owns() && getWork() === current && getDeliveryAction(current) === lease,
      });
      clearDeliveryWorkspace(current, lease);
      if (owns()) {
        setOfflinePromptOpen(false);
        toast.success('Copy saved on this device. The app cannot reopen its contents or resend this older format. Saving does not confirm delivery.');
        onClose?.();
      }
    } catch (error) {
      if (owns()) toast.error(error?.code === 'LEGACY_COPY_EXISTS' ? error.message
        : 'Could not save to your device. Your work is still here; try again.');
    } finally { releaseDeliveryAction(lease); }
  };
  const handlePhotoCapture = (event) => {
    if (!canAct() || getDeliveryAction(getWork())) return;
    if (DELIVERY_PHOTO_PREPARATION_ENABLED) {
      try { validatePreparationBatch([...getWork().fields.photos.map((p) => p.file), ...Array.from(event.target.files || [])]); }
      catch (error) { toast.error(error.message); event.target.value = ''; return; }
    }
    const photos = Array.from(event.target.files || []).map((file) => ({ file, preview: URL.createObjectURL(file) }));
    if (!change('photos', [...getWork().fields.photos, ...photos])) photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
    event.target.value = '';
  };
  const handleVideoCapture = (event) => {
    if (!canAct() || getDeliveryAction(getWork())) return;
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { toast.error('Video too large. Max 100MB (about 60 seconds)'); return; }
    const video = { file, preview: URL.createObjectURL(file) };
    if (!change('video', video)) URL.revokeObjectURL(video.preview);
    event.target.value = '';
  };
  const handleClose = () => {
    if (!owns() || getDeliveryAction(getWork())) return;
    if (photoRecovery.isOpen) { photoRecovery.close(); return; }
    if (DELIVERY_PHOTO_PREPARATION_ENABLED && !photoPreparation.saved && !photoRecovery.savedCurrent
        && (hasDeliveryWorkspaceWork(getWork()) || photoPreparation.pendingSave)) {
      toast.error('Changes are not saved to this device. Keep this tab open to preserve them.'); return;
    }
    listDeliveryWorkspaces(opening.principal, opening.orderId).forEach((candidate) => {
      if (!hasDeliveryWorkspaceWork(candidate)) clearDeliveryWorkspace(candidate);
    });
    onClose?.();
  };
  const handleCancelEdits = async () => {
    if (DELIVERY_PHOTO_PREPARATION_ENABLED) return;
    const current = getWork();
    if (!isPlainEditDraft || !current || current.attempt || current.savedMediaRemoval) return;
    const admission = admit('amendment', 'discard-draft');
    if (!admission) return;
    const { lease, token } = admission;
    let discarded = false;
    try {
      // Keep the draft until a fresh, correctly scoped saved record is available.
      const result = await getOrderDeliveryConfirmation(opening.orderId, { token });
      if (!owns() || getWork() !== current) return;
      const dc = result?.confirmation;
      if (result?.order_id !== opening.orderId || result.status !== 'delivered'
        || !dc || typeof dc !== 'object' || Array.isArray(dc)
        || typeof dc.received_by !== 'string'
        || ['received_by_relationship', 'notes', 'time_delivered'].some((key) => dc[key] != null && typeof dc[key] !== 'string')) {
        throw new Error('Invalid saved delivery');
      }
      if (!clearDeliveryWorkspace(current, lease)) return;
      const reset = createDeliveryWorkspace(opening.principal, opening.orderId, 'amendment', initialFields({ delivery_confirmation: dc }, true));
      // The reset is a new workspace; release the old revision's lease before
      // recording its fresh baseline. No asynchronous gap admits another action.
      releaseDeliveryAction(lease);
      updateDeliveryWorkspace(reset, { checkpoint: { baselineText: buildDeliveryEditPayload(reset.fields) } });
      setRecoveryOpening(null);
      setReviewedOpening(null);
      setUncertaintyReviewed(null);
      reviewedUnknown.current = null;
      setUploadProgress(0);
      discarded = true;
    } catch {
      if (owns()) toast.error('Could not load the saved delivery. Your edits are still here; try Cancel again.');
    } finally { releaseDeliveryAction(lease); }
    if (discarded && owns()) await savedMedia.refresh();
  };
  const canDiscardPhotoDraft = DELIVERY_PHOTO_PREPARATION_ENABLED && !photoRecovery.isOpen && owns() && photoPreparation.ready && photoPreparation.saved
    && !photoPreparation.busy && !!work && (!work.attempt || acceptedFailure) && !work.savedMediaRemoval && !getDeliveryAction(work)
    && !kindConflict && hasDeliveryWorkspaceWork(work);
  const handleDiscardPhotoDraft = async () => {
    const selected = discardSelection;
    if (!selected || selected.opening !== opening || selected.work !== getWork() || !canDiscardPhotoDraft) {
      setDiscardSelection(null); return;
    }
    const lease = acquireDeliveryAction(selected.work, 'discard-photo-draft');
    if (!lease) return;
    try {
      await photoPreparation.discardLocal(selected.work, lease);
      if (!owns() || getWork() !== selected.work) return;
      clearDeliveryWorkspace(selected.work, lease);
      setDiscardSelection(null);
      toast.success('Local draft removed from this device. Camera-roll and server photos were not changed.');
      onClose?.();
    } catch (error) { if (owns()) { setDiscardSelection(null); setPhotoOutcome(error.message || 'Draft retained. Nothing was discarded.'); } }
    finally { releaseDeliveryAction(lease); }
  };
  const outcomeDetail = uncertainAttempt?.message || work?.attempt?.message || failureWork?.attempt?.message
    || (!work && readyOpening === opening ? 'The previous action completed. Close this form and refresh the order before doing more work.' : '');
  const acknowledgedTextMessage = work?.checkpoint?.acknowledgedText
    ? 'Text changes were accepted. Any remaining media must be completed separately; acknowledged unchanged text will not be sent again.' : '';

  return {
    ...fields, isVisible, isEditing, isSubmitting, uploadProgress, savedMedia, photoPreparation, photoRecovery, acceptedFailure,
    clearLocation,
    checkingPhotoStatus: ['check-photo-receipt', 'check-failure-receipt'].includes(getDeliveryAction(work)?.action),
    deliveryAccepted: !isFailureAttempt && work?.attempt?.status === 'accepted',
    canRetryFailure: isFailureAttempt && !acceptedFailure && canAct() && photoPreparation.saved && !photoPreparation.busy && !isSubmitting,
    isFormValid: !isFailureAttempt && (!DELIVERY_PHOTO_PREPARATION_ENABLED || (photoPreparation.ready && photoPreparation.saved && !photoPreparation.busy && (!photoPreparation.otherDraft || explicitlySelected) && (!isEditing || !!work?.checkpoint?.amendmentRevision || !!work?.attempt))) && !!fields.receivedBy.trim() && readyOpening === opening && !needsRecovery && !needsUncertaintyReview && !kindConflict && !!work && !work.savedMediaRemoval,
    controlsDisabled: !!discardSelection || isSubmitting || (DELIVERY_PHOTO_PREPARATION_ENABLED && (!photoPreparation.ready || (photoPreparation.otherDraft && !explicitlySelected) || !!work?.attempt || (isEditing && !work?.checkpoint?.amendmentRevision))) || needsRecovery || needsUncertaintyReview || kindConflict || !work || !!work.savedMediaRemoval,
    canDiscardPhotoDraft,
    discardPhotoDraftOpen: discardSelection?.opening === opening && discardSelection?.work === work,
    handleRequestPhotoDiscard: () => { if (canDiscardPhotoDraft) setDiscardSelection({ opening, work }); },
    handleCancelPhotoDiscard: () => { if (owns() && !isSubmitting) setDiscardSelection(null); },
    handleDiscardPhotoDraft,
    needsRecovery, needsUncertaintyReview, kindConflict, hasPendingEdits, handleCancelEdits,
    outcomeMessage: [acknowledgedTextMessage, outcomeDetail, photoOutcome,
      isFailureAttempt ? `Failure report: ${work.attempt.status}. Photos remain local; they were not included in this report.` : ''].filter(Boolean).join(' '),
    canCheckPhotoReceipt: DELIVERY_PHOTO_PREPARATION_ENABLED && !!work?.attempt && needsUncertaintyReview && photoPreparation.ready && photoPreparation.saved && !needsRecovery && !kindConflict,
    handleCheckPhotoReceipt: () => isFailureAttempt ? submitPreparedFailure(true) : submitPreparedDelivery(true),
    handleChooseSavedOperation: () => {
      if (!owns() || discardSelection || !DELIVERY_PHOTO_PREPARATION_ENABLED || !photoPreparation.otherDraft || getDeliveryAction(getWork())) return;
      setSelectedOperation({ base: baseOpening, kind: photoPreparation.otherDraft.kind });
    },
    handleResume: () => { if (owns() && !isSubmitting) setReviewedOpening(opening); },
    handleReviewUncertainty: () => { if (DELIVERY_PHOTO_PREPARATION_ENABLED) return; if (owns() && !getDeliveryAction(getWork())) { reviewedUnknown.current = uncertainAttempt; setUncertaintyReviewed(uncertainAttempt); setOfflinePromptOpen(false); } },
    handleClose, handleSubmit: () => submit(opening.kind), handleFail: () => submit('failure'),
    failureReason: DELIVERY_PHOTO_PREPARATION_ENABLED ? fields.failureReason || '' : failureWork?.fields.reason || '',
    failureDetails: DELIVERY_PHOTO_PREPARATION_ENABLED ? fields.failureDetails || '' : failureWork?.fields.details || '',
    setFailureReason: (value) => DELIVERY_PHOTO_PREPARATION_ENABLED ? change('failureReason', value) : change('reason', value, 'failure'),
    setFailureDetails: (value) => DELIVERY_PHOTO_PREPARATION_ENABLED ? change('failureDetails', value) : change('details', value, 'failure'),
    setReceivedBy: (value) => change('receivedBy', value), setReceivedByRelationship: (value) => change('receivedByRelationship', value),
    setNotes: (value) => change('notes', value), setDeliveryTime: (value) => change('deliveryTime', value),
    setSignatureData: (value) => change('signatureData', value),
    photoInputRef, videoInputRef, handlePhotoCapture, handleVideoCapture,
    removePhoto: (index) => change('photos', getWork()?.fields.photos.filter((_, i) => i !== index) || []),
    handleUseOriginalPhoto: DELIVERY_PHOTO_PREPARATION_ENABLED ? (index) => {
      const photos = getWork()?.fields.photos;
      if (!photos?.[index]?.preparationError || photos[index].prepared) return;
      change('photos', photos.map((photo, i) => i === index ? { ...photo, useOriginal: true } : photo));
    } : undefined,
    removeVideo: () => change('video', null),
    offlinePromptOpen, handleSaveOffline,
    canSaveSnapshot: !discardSelection && (!DELIVERY_PHOTO_PREPARATION_ENABLED ? !isEditing && work?.attempt?.status === 'unknown'
      : photoPreparation.ready && photoPreparation.saved && work?.attempt?.status === 'unknown') && !needsRecovery && !kindConflict,
    handleShowSaveOffline: () => { if (owns() && !discardSelection && !getDeliveryAction(getWork()) && (DELIVERY_PHOTO_PREPARATION_ENABLED || !isEditing) && getWork()?.attempt?.status === 'unknown' && !needsRecovery && !kindConflict) setOfflinePromptOpen(true); },
    handleDismissOffline: () => { if (owns() && !isSubmitting) setOfflinePromptOpen(false); },
  };
};
